import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../store/hooks';
import { loginUser, getUserProfile, clearRedirectUrl } from '../../store/slices/authSlice';
import { Eye, EyeOff, X } from 'lucide-react';
import toast from 'react-hot-toast';
import useEmailFieldValidation from '../../hooks/useEmailFieldValidation';
import { getEmailValidationError, getEmailValidationInputClass } from '../../utils/emailValidation';
import EmailFieldFeedback from '../EmailInput/EmailFieldFeedback';

/**
 * Session Expired Modal
 * Shows when user's session expires during booking flow
 * Allows re-login without losing booking data
 */
const SessionExpiredModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const {
    emailTouched,
    emailError,
    onEmailChange,
    onEmailBlur,
    validateEmailForSubmit,
  } = useEmailFieldValidation();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'email') {
      onEmailChange(value);
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const emailValidationMessage = getEmailValidationError(formData.email);
    validateEmailForSubmit(formData.email);
    if (emailValidationMessage) {
      newErrors.email = emailValidationMessage;
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 5) {
      newErrors.password = 'Password must be at least 5 characters';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);

    try {
      // Attempt login
      const result = await dispatch(loginUser({
        email: formData.email,
        password: formData.password,
        rememberMe: false
      })).unwrap();

      // Check if 2FA is required
      if (result.requires2FA) {
        toast.error('2FA is required. Please complete 2FA verification first.');
        setLoading(false);
        return;
      }

      // Fetch user profile to ensure complete data
      await dispatch(getUserProfile()).unwrap();

      // IMPORTANT: Clear the redirectUrl to prevent default redirect behavior
      dispatch(clearRedirectUrl());

      toast.success('Successfully logged back in!');
      
      // Small delay to ensure Redux state is updated
      setTimeout(() => {
        // Call success callback if provided
        if (onLoginSuccess && typeof onLoginSuccess === 'function') {
          onLoginSuccess();
        }
        
        // Close modal
        onClose();
      }, 100);

    } catch (error) {
      console.error('❌ Re-login failed:', error);
      toast.error(error || 'Login failed. Please check your credentials.');
      setErrors({ general: error || 'Login failed' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <X size={24} />
        </button>

        <div className="p-6">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Session Expired</h2>
            <p className="text-gray-600 text-sm">
              Your session has expired. Please log in again to continue with your booking.
              <br />
              <span className="font-semibold text-jet-yellow">Don't worry - your booking data is safe!</span>
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {errors.general && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {errors.general}
              </div>
            )}

            <div>
              <input
                type="email"
                name="email"
                placeholder="EMAIL"
                value={formData.email}
                onChange={handleChange}
                onBlur={(e) => onEmailBlur(e.target.value)}
                className={getEmailValidationInputClass(
                  'w-full px-4 py-3 border rounded-md text-sm bg-white text-gray-800 placeholder:text-gray-400 uppercase transition-colors duration-300 focus:outline-none focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)]',
                  {
                    touched: emailTouched,
                    error: emailError,
                    value: formData.email,
                    defaultBorderClass: 'border-gray-300 focus:border-jet-yellow',
                  }
                )}
                style={{ textTransform: 'uppercase' }}
              />
              <EmailFieldFeedback
                touched={emailTouched}
                error={emailError || errors.email}
                value={formData.email}
              />
            </div>

            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-md text-sm bg-white text-gray-800 placeholder:text-gray-400 transition-colors duration-300 focus:outline-none focus:border-jet-yellow focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)]"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">{errors.password}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-jet-yellow text-gray-900 border-0 py-3 px-6 rounded-md text-sm font-semibold cursor-pointer transition-all duration-300 hover:bg-jet-yellow-hover hover:-translate-y-0.5 focus:outline-none focus:shadow-[0_0_0_2px_rgba(255,215,0,0.4)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'LOGGING IN...' : 'LOGIN & CONTINUE'}
            </button>
          </form>

          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">
              Your booking information including passenger details and flight selection will be restored after login.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionExpiredModal;
